import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { AppContext } from "../app/store";
import {
  addNewRelicPageAction,
  hasGlobalSession,
  logError,
  logout,
  useCypressEnv,
  useTranslate,
} from "../helpers";
import { getGlobalSessionInfo, sendKeepAlive } from "../api";
import { FormattedMessage } from "react-intl-phraseapp";
import { RouteComponentProps, withRouter } from "react-router";
import routes from "../app/routes";
import { NewRelicPageActionNames } from "../@types";
import { ApiError } from "../helpers/errors";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
} from "../app/contexts/Analytics/types";

export const IdleTimeout: FunctionComponent<RouteComponentProps> = ({
  children,
  location,
}) => {
  enum IdleTimeoutActivityState {
    ACTIVE = "active",
    IDLE = "idle",
    LOGGED_OUT = "logged_out",
  }

  const {
    state: { beneficiary, lastActivity },
    state,
    dispatch,
  } = useContext(AppContext);
  const t = useTranslate();
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const cypressEnv = useCypressEnv();
  const disableIdleTimout = cypressEnv.DISABLE_IDLE_TIMEOUT;

  // Constants
  const ONE_MINUTE_MS = 1000 * 60;
  const ACTIVITY_THROTTLE_MS = ONE_MINUTE_MS;
  const FIVE_MINUTE_MS = ONE_MINUTE_MS * 5;
  const CSR_IDLE_TIMEOUT_MS = ONE_MINUTE_MS * 15;
  const CSR_FORCE_LOGOUT_TIMEOUT_MS = FIVE_MINUTE_MS;

  // State
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutModalShown, setForceLogoutModalShown] = useState(false);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState<{ value: number }>({
    value: ONE_MINUTE_MS,
  }); // Set the initial value to one minute to prevent pop-up from showing immediately
  const [forceLogoutTimeoutMs, setForceLogoutTimeoutMs] = useState<{
    value: number;
  }>({
    value: 0,
  });
  const [activityState, setActivityState] = useState<
    IdleTimeoutActivityState
  >();

  // Refs
  const hasSessionWithBeneficiary = hasGlobalSession(state);
  const isLoggedInRef = useRef(hasSessionWithBeneficiary);
  const logoutTimeoutRef = useRef<NodeJS.Timeout>();

  // Hooks

  // This `useEffect` kicks off the IdleTimeout handler
  useEffect(() => {
    if (disableIdleTimout) return;

    isLoggedInRef.current = hasSessionWithBeneficiary;

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    if (hasSessionWithBeneficiary) {
      setActivityState(IdleTimeoutActivityState.ACTIVE);
    }
  }, [hasSessionWithBeneficiary, disableIdleTimout]);

  useEffect(() => {
    setShowForceLogoutModal(false);

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    if (isLoggedInRef.current) {
      console.debug(`☣️ starting timer`);
      startTimer();
    }
  }, [idleTimeoutMs]);

  useEffect(() => {
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    if (activityState === IdleTimeoutActivityState.IDLE) {
      setShowForceLogoutModal(true);

      pauseTimer();

      logoutTimeoutRef.current = setTimeout(() => {
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.SYSTEM_END_IDLE_SESSION,
          },
        });

        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_label: "mct_plan_finder_system_end_idle_session",
            event_action: "idle session - system ended session",
            other_props: {
              beneficiary_key: beneficiary?.meta_data.beneficiary_key,
              csr_id: beneficiary?.csr_id,
            },
          },
        });

        setActivityState(IdleTimeoutActivityState.LOGGED_OUT);
      }, forceLogoutTimeoutMs.value);
    }
  }, [forceLogoutTimeoutMs]);

  useEffect(() => {
    if (showForceLogoutModal) {
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.SHOW_IDLE_SESSION_MODAL,
        },
      });

      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_label: "mct_plan_finder_show_idle_session_modal",
          event_action: "idle session - modal impression",
          other_props: {
            beneficiary_key: beneficiary?.meta_data.beneficiary_key,
            csr_id: beneficiary?.csr_id,
          },
        },
      });

      setForceLogoutModalShown(true);
    }
  }, [showForceLogoutModal]);

  useEffect(() => {
    if (isLoggedInRef.current && !isMbpLandingRoute && !disableIdleTimout) {
      // mbpLandingRoute always redirects, so don't try this on that route
      if (activityState === IdleTimeoutActivityState.ACTIVE) {
        startIdleTimer();
      } else if (activityState === IdleTimeoutActivityState.IDLE) {
        startForceLogoutTimer();
      } else if (activityState === IdleTimeoutActivityState.LOGGED_OUT) {
        closeSession();
      }
    }
  }, [
    activityState,
    isLoggedInRef.current,
    isMbpLandingRoute,
    disableIdleTimout,
  ]);

  // Methods
  const msToMinutes = (num: number) => num / 1000 / 60;

  const keepSessionAlive = async () => {
    if (isLoggedInRef.current) {
      try {
        await sendKeepAlive();
      } catch (err) {
        const e = err as ApiError;
        const message = e.message || "";
        logError(`Error calling /beneinfo/keep-alive: ${message}`, e);
        setActivityState(IdleTimeoutActivityState.LOGGED_OUT);
      }
    }
  };

  const getBeneSessionInfo = async () => {
    const info = {
      sessionExpirationInMs: 0,
      idleTimeoutMs: 0,
      forceLogoutTimeoutMs: 0,
      loggedOut: false,
    };

    try {
      const session = await getGlobalSessionInfo();

      if (session) {
        const { exp: sessionExpirationInSeconds } = session;
        const sessionExpirationInMs = sessionExpirationInSeconds * 1000;
        const timeUntilSessionExpiresMs = sessionExpirationInMs - Date.now();
        info.sessionExpirationInMs = sessionExpirationInMs;

        if (timeUntilSessionExpiresMs > FIVE_MINUTE_MS) {
          info.idleTimeoutMs = timeUntilSessionExpiresMs - FIVE_MINUTE_MS;
        } else {
          info.forceLogoutTimeoutMs = timeUntilSessionExpiresMs;
        }
      }
    } catch (err) {
      const e = err as ApiError;
      const message = e.message || "";
      logError(`Error calling /api/sls/session/info: ${message}`, e);
      info.loggedOut = true;
    }

    return info;
  };

  const startIdleTimer = async () => {
    if (!beneficiary?.csr_id) {
      const {
        idleTimeoutMs,
        forceLogoutTimeoutMs,
        loggedOut,
      } = await getBeneSessionInfo();

      if (loggedOut) {
        logout({ dispatch });
      } else if (idleTimeoutMs > 0) {
        setIdleTimeoutMs({ value: idleTimeoutMs });
      } else if (forceLogoutTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.IDLE);
      }
    } else {
      setIdleTimeoutMs({ value: CSR_IDLE_TIMEOUT_MS });
    }
  };

  const startForceLogoutTimer = async () => {
    if (!beneficiary?.csr_id) {
      const {
        idleTimeoutMs,
        forceLogoutTimeoutMs,
        loggedOut,
      } = await getBeneSessionInfo();

      if (loggedOut) {
        logout({ dispatch });
      } else if (idleTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.ACTIVE);
      } else if (forceLogoutTimeoutMs > 0) {
        setForceLogoutTimeoutMs({ value: forceLogoutTimeoutMs });
      }
    } else {
      setForceLogoutTimeoutMs({ value: CSR_FORCE_LOGOUT_TIMEOUT_MS });
    }
  };

  const closeSession = async () => {
    if (!beneficiary?.csr_id) {
      const {
        idleTimeoutMs,
        forceLogoutTimeoutMs,
      } = await getBeneSessionInfo();

      if (idleTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.ACTIVE);

        return;
      } else if (forceLogoutTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.IDLE);

        return;
      }
    }

    addNewRelicPageAction(NewRelicPageActionNames.LOGOUT, {
      activityId: beneficiary?.activity_id || "",
      beneficiaryKey: beneficiary?.meta_data?.beneficiary_key || "",
      csrId: beneficiary?.csr_id || "",
      currentTimeUtc: new Date().toUTCString(),
      forceLogoutModalShown: `${forceLogoutModalShown}`,
      forceLogoutTimeoutMs: `${forceLogoutTimeoutMs.value}`,
      hasSessionAndBene: `${hasGlobalSession(state)}`,
      isLoggedInRef: `${isLoggedInRef.current}`,
      lastActivityUtc: lastActivity?.toUTCString() || "",
    });

    logout({ dispatch });
  };

  const extendSessionAndContinue = async () => {
    await keepSessionAlive();

    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.CONTINUE_IDLE_SESSION,
      },
    });

    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_label: "mct_plan_finder_continue_idle_session",
        event_action: "idle session - continue session",
        other_props: {
          beneficiary_key: beneficiary?.meta_data.beneficiary_key,
          csr_id: beneficiary?.csr_id,
        },
      },
    });

    setActivityState(IdleTimeoutActivityState.ACTIVE);
  };

  const { pause: pauseTimer, start: startTimer } = useIdleTimer({
    timeout: idleTimeoutMs.value,
    onIdle: () => {
      if (isLoggedInRef.current) {
        setActivityState(IdleTimeoutActivityState.IDLE);
      }
    },
    onAction: keepSessionAlive,
    startManually: true,
    throttle: ACTIVITY_THROTTLE_MS,
  });

  return (
    <>
      {showForceLogoutModal && (
        <Dialog
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("App")
          }
          escapeExits={false}
          className="UserActivityMonitor__modal"
          heading={t("user_activity.title")}
          onExit={extendSessionAndContinue}
          actions={[
            <Button
              variation="solid"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.PRIMARY,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.continue_session"),
                    },
                  },
                });
                extendSessionAndContinue();
              }}
              key="primary"
            >
              {t("user_activity.continue_session")}
            </Button>,
            <Button
              variation="ghost"
              key="cancel"
              onClick={(): void => {
                dispatch({
                  type: AnalyticsActionType.SEND_GA4_EVENT,
                  settings: {
                    event_name: Ga4Event.USER_END_IDLE_SESSION,
                  },
                });

                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_label: "mct_plan_finder_user_end_idle_session",
                    event_action: "idle session - user ended session",
                    other_props: {
                      beneficiary_key: beneficiary?.meta_data.beneficiary_key,
                      csr_id: beneficiary?.csr_id,
                    },
                  },
                });

                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.logout"),
                    },
                  },
                });
                logout({
                  dispatch,
                });
              }}
            >
              {t("user_activity.logout")}
            </Button>,
          ]}
        >
          <FormattedMessage
            id="idle_timeout.inactive"
            values={{
              minutes: Math.ceil(msToMinutes(forceLogoutTimeoutMs.value)),
            }}
          />
        </Dialog>
      )}
      {children}
    </>
  );
};

export default withRouter(IdleTimeout);
